import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
import "../../../../styles/scss/recital2027CustomerList.scss";

/**
 * 発表会2027（SLASH GALLERY vol.5）参加者一覧
 *
 * 設計方針:
 * - 参加/不参加を上部タブで切替（participation_flg）。
 * - 不参加タブには「申込で不参加を選んだ人(source=application)」に加えて、
 *   「申込はしていないが開催月(2027-02)に休会中の在籍会員(source=rest)」も並ぶ。
 *   休会中は発表会に出られないため実質の不参加。後者は不参加理由・申込日時が空。
 * - 受講レッスン(参加ナンバー)は申込時の numbers ではなく、API が現在プラン(plans)
 *   から解決した `lessons` を表示する（numbers は廃止予定）。
 * - 一覧は要点列のみ。座席アンケート・追加請求・請求書URL等の詳細はドロワーに集約。
 * - カラム構造が変わる前提のため、ラベル/型は FIELDS に一元化し、列定義・ドロワーを
 *   データ駆動で描画。API が返す未知フィールドはドロワー「その他」に自動表示される。
 */

// 支払状態(status / pickup_invoice_status)の日本語ラベル
const STATUS_LABELS = {
  pending: "未払い",
  paid: "支払済",
  declined: "辞退",
};

// ピックアップ支払い状況の派生状態。発表会の支払いは全て請求書で後払い(申込時の即支払いは無い)。
// pickup_invoice_id の有無で「申込時PU(本体請求に含む) / 追加PU(別請求書)」を区別し、
// 支払可否は 申込時PU=本体請求status / 追加PU=pickup_invoice_status で判定する。
const PICKUP_STATES = {
  included_paid: { label: "申込時・支払済", cls: "pickup-paid" },
  included_unpaid: { label: "申込時・未払", cls: "pickup-unpaid" },
  addon_paid: { label: "追加・支払済", cls: "pickup-paid" },
  addon_unpaid: { label: "追加・未払", cls: "pickup-unpaid" },
  none: { label: "なし", cls: "pickup-none" },
};

const pickupState = (row) => {
  if (!row || !row.pickup_flg) return "none";
  if (!row.pickup_invoice_id) {
    // 申込時にPU選択 → 本体請求書の支払状況で判定
    return row.status === "paid" ? "included_paid" : "included_unpaid";
  }
  // 後から追加 → 追加請求書の支払状況で判定
  return row.pickup_invoice_status === "paid" ? "addon_paid" : "addon_unpaid";
};

// フィールドのラベルと表示型を一元管理（カラム変更時はここを編集）
const FIELDS = {
  id: { label: "ID", type: "mono" },
  customer_id: { label: "会員ID", type: "mono" },
  name: { label: "氏名", type: "name" },
  name_yomi: { label: "フリガナ", type: "text" },
  mail: { label: "メール", type: "mail" },
  participation_flg: { label: "参加", type: "bool", trueLabel: "参加", falseLabel: "不参加" },
  lessons: { label: "参加クラス", type: "lessons" },
  pickup_flg: { label: "ピックアップ", type: "pickup" },
  pickup_areas: { label: "希望エリア", type: "json" },
  pamphlet_image: { label: "パンフ画像", type: "submission" },
  pass_flg: { label: "関係者パス", type: "bool", trueLabel: "あり", falseLabel: "なし" },
  seat_s_count: { label: "S席希望", type: "num" },
  seat_a_count: { label: "A席希望", type: "num" },
  seat_b_count: { label: "B席希望", type: "num" },
  opinion: { label: "不参加理由", type: "text" },
  subtotal: { label: "小計(税別)", type: "money" },
  discount: { label: "値引額", type: "money" },
  tax: { label: "消費税", type: "money" },
  total_pay: { label: "合計(税込)", type: "money" },
  status: { label: "支払状況", type: "badge", valueLabels: STATUS_LABELS },
  stripe_customer_id: { label: "Stripe顧客ID", type: "mono" },
  invoice_id: { label: "請求書ID", type: "mono" },
  invoice_url: { label: "請求書URL", type: "url" },
  pickup_invoice_id: { label: "ピックアップ追加請求ID", type: "mono" },
  pickup_invoice_url: { label: "ピックアップ追加請求URL", type: "url" },
  pickup_invoice_status: { label: "ピックアップ追加請求状態", type: "badge", valueLabels: STATUS_LABELS },
  source: {
    label: "区分",
    type: "source",
    valueLabels: { application: "申込", rest: "休会のみ" },
  },
  rest_period: { label: "休会期間", type: "rest" },
  created_at: { label: "申込日時", type: "datetime" },
  updated_at: { label: "更新日時", type: "datetime" },
  delete_flg: { label: "削除", type: "bool", trueLabel: "削除", falseLabel: "—" },
};

const fieldMeta = (key) => FIELDS[key] || { label: key, type: "text" };

// タブごとの一覧列（key + 表示幅 + ソート/フィルタ可否）
const PARTICIPATING_COLUMNS = [
  { key: "id", width: "64px", sortable: true },
  { key: "customer_id", width: "84px", sortable: true },
  { key: "name", width: "minmax(110px, 1.1fr)", sortable: true },
  { key: "name_yomi", width: "minmax(96px, 0.9fr)", sortable: true },
  { key: "lessons", width: "minmax(240px, 2.2fr)" },
  { key: "pickup_flg", width: "148px" },
  { key: "pickup_areas", width: "minmax(120px, 1.2fr)" },
  { key: "pamphlet_image", width: "104px" },
  { key: "pass_flg", width: "92px", sortable: true },
  { key: "total_pay", width: "104px", sortable: true },
  { key: "status", width: "110px", sortable: true },
];

const DECLINED_COLUMNS = [
  { key: "customer_id", width: "84px", sortable: true },
  { key: "name", width: "minmax(110px, 1fr)", sortable: true },
  { key: "name_yomi", width: "minmax(96px, 0.9fr)", sortable: true },
  { key: "source", width: "104px", sortable: true },
  { key: "rest_period", width: "160px", sortable: true },
  { key: "mail", width: "minmax(170px, 1.2fr)" },
  { key: "opinion", width: "minmax(220px, 2.2fr)", sortable: true },
  { key: "created_at", width: "150px", sortable: true },
];

// 参加タブのフィルタ定義（列に縛られず、件数やエリアなどの派生条件も持てる）
const PARTICIPATING_FILTERS = [
  { key: "pickup", label: "ピックアップ", type: "pickup" },
  { key: "pass_flg", label: "関係者パス", type: "bool", trueLabel: "あり", falseLabel: "なし" },
  { key: "status", label: "支払状況", type: "enum", field: "status", valueLabels: STATUS_LABELS },
  { key: "lesson_count", label: "参加クラス数", type: "lesson_count" },
  { key: "instructor", label: "インストラクター", type: "instructor" },
  { key: "area", label: "希望エリア", type: "area" },
  { key: "submitted", label: "パンフ画像", type: "submitted" },
  { key: "unpaid", label: "未払い", type: "unpaid" },
];

// 不参加タブのフィルタ。「申込で不参加にした人のうち休会登録が漏れている人」を
// すぐ洗い出せるよう、区分と休会登録の有無で絞れるようにする。
const DECLINED_FILTERS = [
  {
    key: "source",
    label: "区分",
    type: "source_enum",
    field: "source",
    valueLabels: FIELDS.source.valueLabels,
    options: ["application", "rest"],
  },
  { key: "rest_registered", label: "休会登録", type: "rest_registered" },
];

// 本体請求(status) または ピックアップ追加請求(pickup_invoice_status)が未払いか
const isUnpaid = (row) => {
  const mainUnpaid = row.status !== "paid";
  const addonUnpaid = !!row.pickup_invoice_id && row.pickup_invoice_status !== "paid";
  return mainUnpaid || addonUnpaid;
};

// 申込状況の参加率に応じた色分け（成績の良し悪し）。所属0は中立。
const rateClass = (s) => {
  if (!s.belong_count) return "rate-none";
  const r = (s.applied_count / s.belong_count) * 100;
  if (r >= 70) return "rate-high";
  if (r >= 40) return "rate-mid";
  return "rate-low";
};

// ドロワーのセクション構成（順序つき）。ここに無い未知キーは「その他」へ自動表示。
const DRAWER_SECTIONS = [
  { title: "申込内容", keys: ["pickup_flg", "pickup_areas", "pass_flg", "pamphlet_image"] },
  { title: "座席アンケート", keys: ["seat_s_count", "seat_a_count", "seat_b_count"] },
  {
    title: "金額・請求",
    keys: ["subtotal", "discount", "tax", "total_pay", "status", "invoice_url"],
  },
  {
    title: "ピックアップ追加請求",
    keys: ["pickup_invoice_status", "pickup_invoice_url"],
    condition: (r) => !!r.pickup_invoice_id, // 後から追加した人(別請求書あり)のみ表示
  },
];

// ドロワーで個別表示しない（hero / 専用セクションで扱う or 内部用）キー
const DRAWER_HANDLED = new Set([
  "id",
  "customer_id",
  "name",
  "name_yomi",
  "participation_flg",
  "lessons",
  "opinion",
  "delete_flg",
  // ドロワーに出さない（請求書ID/Stripe顧客ID/更新日時は不要）
  "invoice_id",
  "pickup_invoice_id",
  "stripe_customer_id",
  "updated_at",
  // 廃止カラム（モデル削除前に値が残っていても一覧/ドロワーに出さない）
  "numbers",
  "pamphlet_flg",
  // 休会由来の情報はヒーロー/専用セクションで扱う
  "source",
  "rest_period",
  ...DRAWER_SECTIONS.flatMap((s) => s.keys),
]);

// ---------- 表示整形 ----------
const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
};

const lessonsToText = (value) =>
  Array.isArray(value)
    ? value.map((l) => (l && l.name) || "").filter(Boolean).join(" / ")
    : "";

// 検索・ソート用のプレーンテキスト化
const valueToText = (key, value) => {
  const meta = fieldMeta(key);
  if (meta.type === "lessons") return lessonsToText(value);
  if (meta.type === "rest") return value ? String(value) : "";
  if (meta.type === "bool") return value ? meta.trueLabel : meta.falseLabel;
  if (meta.type === "datetime") return formatDateTime(value);
  if (meta.type === "money") return value == null ? "" : `¥${Number(value).toLocaleString()}`;
  if (meta.valueLabels && value !== null && value !== undefined)
    return meta.valueLabels[value] || String(value);
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

function CustomerListPage() {
  useLoginCheck();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("participating"); // participating | declined
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [selected, setSelected] = useState(null);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [applicationStats, setApplicationStats] = useState([]);
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [statsError, setStatsError] = useState("");
  const [statsInstr, setStatsInstr] = useState(null); // 申込状況のインストラクター絞り込み
  const [summary, setSummary] = useState(null); // 会員数/参加/不参加/未回答
  const [seatDemand, setSeatDemand] = useState(null); // 座席の希望数(1部/2部)

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/api/get_recital2027_customers/`);
      if (!res.ok) throw new Error("ネットワークレスポンスが異常です。");
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("発表会2027参加者の取得に失敗しました:", e);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // 回答状況サマリー(会員数/参加/不参加/未回答)。初回に取得。
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/get_recital2027_summary/`);
        if (!res.ok) return;
        const data = await res.json();
        if (!aborted && data && typeof data.member_count === "number") setSummary(data);
      } catch (e) {
        console.error("サマリーの取得に失敗しました:", e);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [apiUrl]);

  // 座席の希望数(1部/2部・両部出演を考慮)。初回に取得。
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/get_recital2027_seat_demand/`);
        if (!res.ok) return;
        const data = await res.json();
        if (!aborted && data && data.parts) setSeatDemand(data);
      } catch (e) {
        console.error("座席の希望数の取得に失敗しました:", e);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [apiUrl]);

  // 申込状況(クラス別 所属/申込/参加率)。開いた時に一度だけ取得。
  useEffect(() => {
    if (!isStatsOpen || statsLoaded) return undefined;
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/get_recital2027_application_stats/`);
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${body.slice(0, 300)}`);
        }
        const data = await res.json();
        if (!aborted) {
          setApplicationStats(Array.isArray(data) ? data : []);
          setStatsError("");
          setStatsLoaded(true);
        }
      } catch (e) {
        console.error("申込状況の取得に失敗しました:", e);
        if (!aborted) {
          setStatsError(String((e && e.message) || e));
          setStatsLoaded(true); // 「読み込み中」固定を解消し、エラーを表示
        }
      }
    })();
    return () => {
      aborted = true;
    };
  }, [isStatsOpen, statsLoaded, apiUrl]);

  // 申込状況のインストラクター一覧と絞り込み結果
  const statsInstructors = useMemo(() => {
    const m = new Map();
    applicationStats.forEach((s) => {
      if (s.instructor_id != null && !m.has(s.instructor_id)) {
        m.set(s.instructor_id, s.instructor_name);
      }
    });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [applicationStats]);

  const filteredStats = useMemo(
    () =>
      statsInstr == null
        ? applicationStats
        : applicationStats.filter((s) => s.instructor_id === statsInstr),
    [applicationStats, statsInstr]
  );

  // タブ切替時はフィルタ/ソートをリセット
  const switchTab = (next) => {
    setTab(next);
    setFilters({});
    setSortConfig({ key: null, direction: "ascending" });
  };

  const columns = tab === "participating" ? PARTICIPATING_COLUMNS : DECLINED_COLUMNS;

  // タブ別の母集合
  const tabRows = useMemo(() => {
    const want = tab === "participating";
    return customers.filter((c) => Boolean(c.participation_flg) === want);
  }, [customers, tab]);

  const counts = useMemo(() => {
    let p = 0;
    let d = 0;
    customers.forEach((c) => (c.participation_flg ? (p += 1) : (d += 1)));
    return { participating: p, declined: d };
  }, [customers]);

  // 不参加の内訳。「申込で不参加にしたのに休会登録が無い人」が休会漏れの候補。
  const declinedBreakdown = useMemo(() => {
    const rows = customers.filter((c) => !c.participation_flg);
    return {
      applied: rows.filter((r) => r.source !== "rest").length,
      rest: rows.filter((r) => r.source === "rest").length,
      noRest: rows.filter((r) => !r.rest_period).length,
    };
  }, [customers]);

  // フィルタの動的選択肢（データから抽出）
  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(tabRows.map((r) => r.status).filter((v) => v !== null && v !== undefined && v !== ""))
      ),
    [tabRows]
  );
  const instructorOptions = useMemo(
    () =>
      Array.from(
        new Set(tabRows.flatMap((r) => (r.lessons || []).map((l) => l.instructor_name)).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "ja")),
    [tabRows]
  );
  const areaOptions = useMemo(
    () =>
      Array.from(
        new Set(
          tabRows.flatMap((r) => (Array.isArray(r.pickup_areas) ? r.pickup_areas : [])).filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, "ja")),
    [tabRows]
  );

  // 現タブで有効なフィルタ。動的選択肢が空のものは出さない。
  const activeFilters = useMemo(() => {
    if (tab !== "participating") return DECLINED_FILTERS;
    return PARTICIPATING_FILTERS.filter((f) => {
      if (f.type === "enum") return statusOptions.length > 0;
      if (f.type === "instructor") return instructorOptions.length > 0;
      if (f.type === "area") return areaOptions.length > 0;
      return true;
    });
  }, [tab, statusOptions, instructorOptions, areaOptions]);

  // フィルタのチップ選択肢 [{value, label}] を生成
  const filterChips = (f) => {
    const all = { value: "all", label: "すべて" };
    switch (f.type) {
      case "pickup":
        return [all, ...Object.entries(PICKUP_STATES).map(([k, v]) => ({ value: k, label: v.label }))];
      case "bool":
        return [all, { value: "true", label: f.trueLabel }, { value: "false", label: f.falseLabel }];
      case "enum":
        return [
          all,
          ...statusOptions.map((v) => ({ value: v, label: (f.valueLabels && f.valueLabels[v]) || v })),
        ];
      case "lesson_count":
        return [all, { value: "single", label: "1クラスのみ" }, { value: "multi", label: "複数クラス" }];
      case "instructor":
        return [all, ...instructorOptions.map((v) => ({ value: v, label: v }))];
      case "area":
        return [all, ...areaOptions.map((v) => ({ value: v, label: v }))];
      case "submitted":
        return [all, { value: "yes", label: "提出済" }, { value: "no", label: "未提出" }];
      case "unpaid":
        return [all, { value: "on", label: "未払いのみ" }];
      case "source_enum":
        return [all, ...f.options.map((v) => ({ value: v, label: f.valueLabels[v] || v }))];
      case "rest_registered":
        return [all, { value: "yes", label: "登録あり" }, { value: "no", label: "登録なし" }];
      default:
        return [all];
    }
  };

  // 絞り込み + ソート
  const filtered = useMemo(() => {
    let rows = [...tabRows];

    const kw = searchKeyword.trim().toLowerCase();
    if (kw) {
      const searchKeys = ["name", "name_yomi", "mail", "customer_id", "id", "status", "opinion", "lessons", "pickup_areas", "rest_period"];
      rows = rows.filter((row) =>
        searchKeys.some((k) => valueToText(k, row[k]).toLowerCase().includes(kw))
      );
    }

    Object.entries(filters).forEach(([key, val]) => {
      if (val === undefined || val === null || val === "all") return;
      const f = activeFilters.find((x) => x.key === key);
      if (!f) return;
      switch (f.type) {
        case "pickup":
          rows = rows.filter((row) => pickupState(row) === val);
          break;
        case "bool":
          rows = rows.filter((row) => Boolean(row[f.key]) === (val === "true"));
          break;
        case "enum":
          rows = rows.filter((row) => String(row[f.field]) === String(val));
          break;
        case "lesson_count":
          rows = rows.filter((row) => {
            const n = (row.lessons || []).length;
            return val === "single" ? n === 1 : n >= 2;
          });
          break;
        case "instructor":
          rows = rows.filter((row) => (row.lessons || []).some((l) => l.instructor_name === val));
          break;
        case "area":
          rows = rows.filter(
            (row) => Array.isArray(row.pickup_areas) && row.pickup_areas.includes(val)
          );
          break;
        case "submitted":
          rows = rows.filter((row) => (val === "yes" ? !!row.pamphlet_image : !row.pamphlet_image));
          break;
        case "unpaid":
          rows = rows.filter((row) => isUnpaid(row));
          break;
        case "source_enum":
          rows = rows.filter((row) => String(row[f.field]) === String(val));
          break;
        case "rest_registered":
          rows = rows.filter((row) => (val === "yes" ? !!row.rest_period : !row.rest_period));
          break;
        default:
          break;
      }
    });

    if (sortConfig.key) {
      const dir = sortConfig.direction === "ascending" ? 1 : -1;
      const k = sortConfig.key;
      rows.sort((a, b) => {
        const av = a[k];
        const bv = b[k];
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return valueToText(k, av).localeCompare(valueToText(k, bv), "ja") * dir;
      });
    }

    return rows;
  }, [tabRows, searchKeyword, filters, sortConfig, activeFilters]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "ascending" ? "descending" : "ascending" }
        : { key, direction: "ascending" }
    );
  };

  const setFilter = (key, val) => setFilters((prev) => ({ ...prev, [key]: val }));

  const handleExportCsv = () =>
    window.open(`${apiUrl}/api/export_recital2027_customers_csv/`, "_blank");

  // drawer: ESC で閉じる
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  const gridTemplateColumns = useMemo(() => columns.map((c) => c.width).join(" "), [columns]);
  const gridMinWidth = useMemo(
    () =>
      columns.reduce((sum, c) => {
        const m = /(\d+)px/.exec(c.width);
        return sum + (m ? parseInt(m[1], 10) : 200);
      }, 0) + 40,
    [columns]
  );

  // ドロワーで「その他」に出す未知/未割当キー（新カラム追加時の自動受け皿）
  const restKeys = useMemo(() => {
    if (!selected) return [];
    return Object.keys(selected).filter((k) => !DRAWER_HANDLED.has(k));
  }, [selected]);

  // ---------- セル描画 ----------
  const renderCell = (col, row) => {
    const meta = fieldMeta(col.key);
    const value = row[col.key];
    if (meta.type === "source") {
      return (
        <span className={`pill source-${value || "application"}`}>
          <span className="dot" />
          {(meta.valueLabels && meta.valueLabels[value]) || value || "—"}
        </span>
      );
    }
    if (meta.type === "rest") {
      // 空 = 休会登録なし。不参加なのに登録漏れの人を見つけるための表示。
      return value || <span className="muted">未登録</span>;
    }
    if (meta.type === "mono" && (value === null || value === undefined)) {
      return <span className="muted">—</span>;
    }
    if (meta.type === "pickup") {
      const info = PICKUP_STATES[pickupState(row)];
      return (
        <span className={`pill ${info.cls}`}>
          <span className="dot" />
          {info.label}
        </span>
      );
    }
    if (meta.type === "submission") {
      return value ? (
        <a
          className="img-btn"
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <Icon name="external alternate" />
          開く
        </a>
      ) : (
        <span className="muted">未提出</span>
      );
    }
    if (meta.type === "bool") {
      return (
        <span className={`pill ${value ? "on" : "off"}`}>
          <span className="dot" />
          {value ? meta.trueLabel : meta.falseLabel}
        </span>
      );
    }
    if (meta.type === "badge") {
      const v = valueToText(col.key, value);
      if (!v) return "";
      const cls = value ? `pill status-${String(value)}` : "pill";
      return <span className={cls}>{v}</span>;
    }
    if (meta.type === "lessons") {
      const txt = lessonsToText(value);
      return txt || <span className="muted">—</span>;
    }
    if (meta.type === "money") {
      return value == null ? "" : `¥${Number(value).toLocaleString()}`;
    }
    return valueToText(col.key, value);
  };

  // ドロワー dd の描画（URLはリンク化）
  const renderDD = (key) => {
    const meta = fieldMeta(key);
    const value = selected[key];
    if (meta.type === "rest") {
      return value || "未登録";
    }
    if (meta.type === "pickup") {
      return PICKUP_STATES[pickupState(selected)].label;
    }
    if (meta.type === "submission") {
      return value ? (
        <a href={value} target="_blank" rel="noopener noreferrer">
          提出済（画像を開く）
        </a>
      ) : (
        "未提出"
      );
    }
    if (meta.type === "url") {
      return value ? (
        <a href={value} target="_blank" rel="noopener noreferrer">
          リンクを開く
        </a>
      ) : (
        "—"
      );
    }
    const txt = valueToText(key, value);
    return txt || "—";
  };

  const renderInfoGrid = (keys) => {
    const present = keys.filter((k) => selected && k in selected);
    if (present.length === 0) return null;
    return (
      <dl className="info-grid">
        {present.map((k) => (
          <React.Fragment key={k}>
            <dt>{fieldMeta(k).label}</dt>
            <dd className={["money", "num", "mono"].includes(fieldMeta(k).type) ? "num" : ""}>
              {renderDD(k)}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    );
  };

  return (
    <div className="recital2027-customer-list-page">
      {/* header */}
      <div className="page-header">
        <div className="header-left">
          <p className="eyebrow">Recital 2027 / SLASH GALLERY vol.5</p>
          <div className="title-row">
            <h1>発表会2027 参加者一覧</h1>
          </div>
        </div>
        <div className="header-right">
          <div className="search-bar">
            <Icon name="search" />
            <input
              type="text"
              placeholder="氏名 / メール / 会員ID / レッスン 等"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
            />
          </div>
          {activeFilters.length > 0 && (
            <button
              className={`header-btn ${isFilterOpen ? "active" : ""}`}
              onClick={() => setIsFilterOpen((p) => !p)}
            >
              <Icon name="filter" />
              フィルタ
            </button>
          )}
          <button
            className={`header-btn ${isStatsOpen ? "active" : ""}`}
            onClick={() => setIsStatsOpen((p) => !p)}
          >
            <Icon name="chart bar" />
            申込状況
          </button>
          <button className="header-btn" onClick={handleExportCsv}>
            <Icon name="download" />
            CSV
          </button>
        </div>
      </div>

      {/* 回答状況サマリー（会員数 / 参加 / 不参加 / 未回答） */}
      {summary && (
        <div className="summary-bar">
          <div className="sm-item">
            <span className="sm-lab">会員数</span>
            <span className="sm-val">
              <span className="n">{summary.member_count}</span>名
            </span>
          </div>
          <div className="sm-item">
            <span className="sm-lab">参加</span>
            <span className="sm-val join">
              <span className="n">{summary.participating}</span>名
            </span>
          </div>
          <div className="sm-item">
            <span className="sm-lab">不参加</span>
            <span className="sm-val decline">
              <span className="n">{summary.declined}</span>名
            </span>
            {typeof summary.declined_rest === "number" && (
              <span className="sm-sub">
                申込 {summary.declined_applied} / 休会のみ {summary.declined_rest}
              </span>
            )}
          </div>
          <div className="sm-item">
            <span className="sm-lab">未回答</span>
            <span className="sm-val pending">
              <span className="n">{summary.unanswered}</span>名
            </span>
          </div>
        </div>
      )}

      {/* 座席の希望数（1部 京都・奈良 / 2部 大阪・兵庫・滋賀）。両部出演の家族は両方に数える */}
      {seatDemand && (
        <div className="seat-demand">
          <div className="sd-head">
            <span className="sd-title">座席の希望数</span>
            <span className="sd-note">
              使用できる席 {seatDemand.capacity}席（1階 {seatDemand.capacity_floor1_total}席から1列目の
              {seatDemand.camera_seats}席〈カメラ席〉を除いた{seatDemand.capacity_floor1}席＋2階{" "}
              {seatDemand.capacity_floor2}席） / 家族（同じメールアドレス）単位・
              {seatDemand.lesson_month.replace("-", "年")}月時点のプランと選抜結果で部を判定
            </span>
          </div>
          {[
            { key: "1", label: "1部", area: "京都・奈良" },
            { key: "2", label: "2部", area: "大阪・兵庫・滋賀" },
          ].map(({ key, label, area }) => {
            const p = seatDemand.parts[key];
            const diff = seatDemand.capacity - p.total;
            return (
              <div className="sd-row" key={key}>
                <span className="sd-part">
                  {label}
                  <small>{area}</small>
                </span>
                <span className="sd-seat s">S <b>{p.S}</b></span>
                <span className="sd-seat a">A <b>{p.A}</b></span>
                <span className="sd-seat b">B <b>{p.B}</b></span>
                <span className="sd-total">
                  計 <b>{p.total}</b>席（{p.families}家族）
                </span>
                <span className={`sd-state ${diff < 0 ? "over" : "ok"}`}>
                  {diff < 0 ? `${-diff}席超過` : `あと${diff}席（関係者席・生徒席に使う想定）`}
                </span>
              </div>
            );
          })}
          <div className="sd-foot">
            両部出演 {seatDemand.both.families}家族（{seatDemand.both.seats}席を1部・2部の両方に数えています）
            {seatDemand.no_part.customers > 0 && (
              <span className="sd-warn">
                ・出演する部が決まらない参加者 {seatDemand.no_part.customers}名（{seatDemand.no_part.seats}席は未集計）
              </span>
            )}
          </div>
          {seatDemand.notes && (
            <ul className="sd-rules">
              <li>
                地域選抜（{seatDemand.notes.regional_customers}名）は、普段のレッスンの部に加えて、選ばれたエリアの部
                （京都・奈良は1部、大阪・兵庫は2部）にも数えています。
              </li>
              <li>
                HEADZ（{seatDemand.notes.headz_customers}名）は、普段のレッスンの部に加えて、1部・2部の両方
                （REINA・YUMEとも両部に出演）に数えています。
                {seatDemand.notes.headz_unassigned_customers > 0 &&
                `セクション未割当の${seatDemand.notes.headz_unassigned_customers}名は、1部・2部の両方に数えています。`}
              </li>
              <li>
                兄弟姉妹で出演する部が違う家族（{seatDemand.notes.split_sibling_families}家族）は、家族の希望席数を
                兄弟姉妹が出るすべての部に数えています。
              </li>
            </ul>
          )}
        </div>
      )}

      {/* tabs */}
      <div className="tab-bar">
        <button
          className={`tab-btn ${tab === "participating" ? "active" : ""}`}
          onClick={() => switchTab("participating")}
        >
          参加者
          <span className="tab-count">{counts.participating}</span>
        </button>
        <button
          className={`tab-btn ${tab === "declined" ? "active" : ""}`}
          onClick={() => switchTab("declined")}
        >
          不参加者
          <span className="tab-count">{counts.declined}</span>
        </button>
      </div>

      {/* filter panel */}
      {isFilterOpen && activeFilters.length > 0 && (
        <div className="filter-panel">
          {activeFilters.map((f) => (
            <div className="filter-group" key={f.key}>
              <span className="filter-label">{f.label}</span>
              <div className="filter-chips">
                {filterChips(f).map((opt) => (
                  <button
                    key={opt.value}
                    className={`filter-chip ${
                      (filters[f.key] || "all") === opt.value ? "active" : ""
                    }`}
                    onClick={() => setFilter(f.key, opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 申込状況（開閉式・クラス別の参加率） */}
      {isStatsOpen && (
        <div className="panel stats-panel">
          <div className="panel-header">
            <div className="section-title">
              <span className="ja">申込状況（クラス別 参加率）</span>
            </div>
            <span className="panel-meta">{filteredStats.length} クラス</span>
          </div>
          {statsInstructors.length > 0 && (
            <div className="stats-instr">
              <button
                className={`filter-chip ${statsInstr == null ? "active" : ""}`}
                onClick={() => setStatsInstr(null)}
              >
                すべて
              </button>
              {statsInstructors.map((i) => (
                <button
                  key={i.id}
                  className={`filter-chip ${statsInstr === i.id ? "active" : ""}`}
                  onClick={() => setStatsInstr(i.id)}
                >
                  {i.name}
                </button>
              ))}
            </div>
          )}
          <div className="panel-body">
            {filteredStats.length === 0 ? (
              <div className="empty-state">
                <p className="ja">
                  {statsError ? "取得に失敗しました" : statsLoaded ? "データがありません" : "読み込み中..."}
                </p>
                <p className="en">{statsError || (statsLoaded ? "No data" : "Loading")}</p>
              </div>
            ) : (
              <div className="stats-grid">
                {filteredStats.map((s) => (
                  <div className="stat-item" key={s.lesson_id}>
                    <div className="stat-class">{s.lesson_name}</div>
                    <div className="stat-metrics">
                      <span className="m">
                        <i>所属</i>
                        {s.belong_count}
                      </span>
                      <span className="m">
                        <i>申込</i>
                        {s.applied_count}
                      </span>
                      <span className={`m rate ${rateClass(s)}`}>
                        <i>参加率</i>
                        {s.rate}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* list */}
      <div className="panel">
        <div className="panel-header">
          <div className="section-title">
            <span className="ja">{tab === "participating" ? "参加者リスト" : "不参加者リスト"}</span>
          </div>
          {tab === "declined" && (
            <span className="panel-meta">
              申込 {declinedBreakdown.applied} / 休会のみ {declinedBreakdown.rest} ・ 休会登録なし{" "}
              <b className={declinedBreakdown.noRest > 0 ? "warn" : ""}>{declinedBreakdown.noRest}</b>
            </span>
          )}
        </div>
        <div className="panel-body">
          {loading ? (
            <div className="empty-state">
              <p className="ja">読み込み中...</p>
              <p className="en">Loading</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <p className="ja">該当する参加者がいません</p>
              <p className="en">No data</p>
            </div>
          ) : (
            <div className="rc-grid" style={{ minWidth: `${gridMinWidth}px` }}>
              <div className="grid-row grid-head" style={{ gridTemplateColumns }}>
                {columns.map((col) => {
                  const meta = fieldMeta(col.key);
                  return (
                    <div
                      key={col.key}
                      className={`cell ${col.sortable ? "sortable" : ""}`}
                      onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    >
                      {meta.label}
                      {sortConfig.key === col.key && (
                        <span className="sort-ind">
                          {sortConfig.direction === "ascending" ? "▲" : "▼"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {filtered.map((row) => (
                <div
                  key={row.id != null ? `a${row.id}` : `r${row.customer_id}`}
                  className="grid-row"
                  style={{ gridTemplateColumns }}
                  onClick={() => setSelected(row)}
                >
                  {columns.map((col) => (
                    <div key={col.key} className={`cell ${fieldMeta(col.key).type}`}>
                      {renderCell(col, row)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* drawer */}
      <div className={`drawer-overlay ${selected ? "open" : ""}`} onClick={() => setSelected(null)} />
      <div className={`rc-drawer ${selected ? "open" : ""}`}>
        {selected && (
          <>
            <button className="drawer-close" onClick={() => setSelected(null)}>
              <Icon name="close" />
            </button>
            <div className="drawer-hero">
              <p className="eyebrow">Recital 2027</p>
              <h2 className="name">{selected.name || "（氏名未取得）"}</h2>
              <p className="sub">
                会員ID: {selected.customer_id ?? "-"}
                {selected.id != null ? ` / ID: ${selected.id}` : " / 申込なし"}
              </p>
              <span className={`hero-badge ${selected.participation_flg ? "on" : "off"}`}>
                {selected.participation_flg ? "参加" : "不参加"}
              </span>
            </div>

            {/* 不参加者の内訳（申込で不参加 / 休会のみ）と休会期間 */}
            {!selected.participation_flg && (
              <div className="drawer-section">
                <div className="section-title">
                  <span className="ja">不参加の内訳</span>
                </div>
                {/* 申込日時は「その他」に既に出るのでここでは重複させない */}
                {renderInfoGrid(["source", "rest_period"])}
              </div>
            )}

            {/* 参加者でも開催月に休会中なら明示する（特例参加の確認用） */}
            {selected.participation_flg && selected.rest_period && (
              <div className="drawer-section">
                <div className="section-title">
                  <span className="ja">休会期間（開催月を含む）</span>
                </div>
                {renderInfoGrid(["rest_period"])}
              </div>
            )}

            {/* 不参加理由 */}
            {!selected.participation_flg && selected.opinion && (
              <div className="drawer-section">
                <div className="section-title">
                  <span className="ja">不参加理由</span>
                </div>
                <p className="msg-block">{selected.opinion}</p>
              </div>
            )}

            {/* 受講レッスン（参加者） */}
            {selected.participation_flg && (
              <div className="drawer-section">
                <div className="section-title">
                  <span className="ja">参加クラス（現在プラン）</span>
                </div>
                {Array.isArray(selected.lessons) && selected.lessons.length > 0 ? (
                  <ul className="lesson-list">
                    {selected.lessons.map((l) => (
                      <li key={l.id}>{l.name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">参加クラスなし（プラン未設定）</p>
                )}
              </div>
            )}

            {/* 構成済みセクション（参加者のみ。不参加者は全員「辞退」・金額0のため非表示） */}
            {selected.participation_flg &&
              DRAWER_SECTIONS.map((sec) => {
                if (sec.condition && !sec.condition(selected)) return null;
                const grid = renderInfoGrid(sec.keys);
                return grid ? (
                  <div className="drawer-section" key={sec.title}>
                    <div className="section-title">
                      <span className="ja">{sec.title}</span>
                    </div>
                    {grid}
                  </div>
                ) : null;
              })}

            {/* その他（未割当・新カラムの自動受け皿） */}
            {restKeys.length > 0 && (
              <div className="drawer-section">
                <div className="section-title">
                  <span className="ja">その他</span>
                </div>
                {renderInfoGrid(restKeys)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CustomerListPage;
